if (!customElements.get('custom-ajax-cart-drawer')) {
  customElements.define(
    'custom-ajax-cart-drawer',
    class CustomAjaxCartDrawer extends HTMLElement {
      constructor() {
        super();
        this.body = this.querySelector('[data-cart-body]');
        this.footer = this.querySelector('[data-cart-footer]');
        this.subtotal = this.querySelector('[data-cart-subtotal]');
        this.panel = this.querySelector('.custom-cart-drawer__panel');
        this.activeElement = null;
        this.cartLoaded = false;
        this.refreshing = false;
      }

      connectedCallback() {
        this.querySelectorAll('[data-cart-close]').forEach((button) => {
          button.addEventListener('click', this.close.bind(this));
        });

        document.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.close();
        });

        this.bindCartIcon();
        this.bindCartLinks();
        if (this.isCartPage()) this.refresh(true);
      }

      bindCartIcon() {
        const cartLink = document.getElementById('cart-icon-bubble');
        if (!cartLink || cartLink.dataset.customCartDrawerBound === 'true') return;

        cartLink.dataset.customCartDrawerBound = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');

        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });

        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() !== 'SPACE') return;
          event.preventDefault();
          this.open(cartLink);
        });
      }

      bindCartLinks() {
        if (document.documentElement.dataset.customCartLinksBound === 'true') return;
        document.documentElement.dataset.customCartLinksBound = 'true';

        document.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link || !this.isCartUrl(link.href)) return;

          event.preventDefault();
          this.open(link);
        });
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      getSectionsToRender() {
        return [{ id: 'cart-icon-bubble' }];
      }

      renderContents(parsedState) {
        this.updateCartBubble(parsedState?.sections?.['cart-icon-bubble']);
        this.refresh(true);
      }

      async refresh(openAfterRefresh = false) {
        if (this.refreshing) return;
        this.refreshing = true;
        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Cart request failed.');
          const cart = await response.json();

          this.renderCart(cart);
          if (openAfterRefresh) this.open();
        } catch (error) {
          console.error('[Custom cart drawer] Could not refresh cart.', error);
          this.body.innerHTML = `<p class="custom-cart-drawer__error">${window.cartStrings?.error || 'Cart could not load.'}</p>`;
        } finally {
          this.refreshing = false;
        }
      }

      renderCart(cart) {
        this.cartLoaded = true;
        this.classList.toggle('is-empty', cart.item_count === 0);
        this.footer.hidden = cart.item_count === 0;
        this.subtotal.textContent = this.formatMoney(cart.total_price, cart.currency);

        if (cart.item_count === 0) {
          this.body.innerHTML = `
            <div class="custom-cart-drawer__empty">
              <p class="custom-cart-drawer__empty-title">Your cart is empty</p>
              <a href="/collections/all" class="button button--primary">Continue shopping</a>
            </div>
          `;
          return;
        }

        this.body.innerHTML = cart.items.map((item, index) => this.getItemMarkup(item, index + 1)).join('');
        this.body.querySelectorAll('[data-cart-quantity]').forEach((button) => {
          button.addEventListener('click', this.onQuantityChange.bind(this));
        });
        this.body.querySelectorAll('[data-cart-remove]').forEach((button) => {
          button.addEventListener('click', this.onRemove.bind(this));
        });
      }

      getItemMarkup(item, line) {
        const image = item.image
          ? `<img class="custom-cart-drawer__image" src="${this.resizeImage(item.image, '160x')}" alt="${this.escapeHtml(item.product_title)}" loading="lazy" width="80" height="80">`
          : `<div class="custom-cart-drawer__image"></div>`;
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="custom-cart-drawer__variant">${this.escapeHtml(item.variant_title)}</p>`
          : '';

        return `
          <div class="custom-cart-drawer__item" data-line="${line}">
            <a href="${item.url}">${image}</a>
            <div>
              <a href="${item.url}" class="custom-cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
              ${variant}
              <p class="custom-cart-drawer__price">${this.formatMoney(item.final_line_price)}</p>
              <div class="custom-cart-drawer__controls">
                <div class="custom-cart-drawer__quantity" aria-label="Quantity">
                  <button type="button" data-cart-quantity data-line="${line}" data-quantity="${Math.max(item.quantity - 1, 0)}" aria-label="Decrease quantity">-</button>
                  <span>${item.quantity}</span>
                  <button type="button" data-cart-quantity data-line="${line}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
                </div>
                <button type="button" class="custom-cart-drawer__remove" data-cart-remove data-line="${line}">
                  Remove
                </button>
              </div>
            </div>
          </div>
        `;
      }

      async onQuantityChange(event) {
        const button = event.currentTarget;
        await this.changeLine(button.dataset.line, Number(button.dataset.quantity));
      }

      async onRemove(event) {
        await this.changeLine(event.currentTarget.dataset.line, 0);
      }

      async changeLine(line, quantity) {
        this.setLoading(true);

        try {
          const response = await fetch(routes.cart_change_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              line: Number(line),
              quantity,
              sections: ['cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });

          const cart = await response.json();
          if (cart.status) throw new Error(cart.description || window.cartStrings?.error);

          this.updateCartBubble(cart.sections?.['cart-icon-bubble']);
          this.renderCart(cart);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'custom-cart-drawer', cartData: cart });
        } catch (error) {
          console.error('[Custom cart drawer] Could not update cart.', error);
          this.body.insertAdjacentHTML(
            'afterbegin',
            `<p class="custom-cart-drawer__error">${this.escapeHtml(error.message || window.cartStrings?.error || 'Cart could not update.')}</p>`
          );
        } finally {
          this.setLoading(false);
        }
      }

      updateCartBubble(html) {
        if (!html) return;
        const source = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#shopify-section-cart-icon-bubble');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
        this.bindCartIcon();
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        this.setAttribute('aria-hidden', 'false');
        this.classList.add('is-open');
        document.body.classList.add('custom-cart-drawer-open');
        if (!this.cartLoaded && !this.refreshing) this.refresh();
        setTimeout(() => this.panel?.focus(), 80);
      }

      close() {
        this.setAttribute('aria-hidden', 'true');
        this.classList.remove('is-open');
        document.body.classList.remove('custom-cart-drawer-open');
        this.activeElement?.focus?.();
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        this.querySelectorAll('button, a').forEach((element) => {
          if (element.matches('.custom-cart-drawer__close, [data-cart-close]')) return;
          element.toggleAttribute('aria-disabled', isLoading);
        });
      }

      resizeImage(src, size) {
        return src.replace(/(\.[^.]*)$/, `_${size}$1`);
      }

      formatMoney(cents, currency) {
        const activeCurrency = currency || window.Shopify?.currency?.active || 'GBP';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: activeCurrency,
        }).format((Number(cents) || 0) / 100);
      }

      isCartPage() {
        return this.isCartPath(window.location.pathname);
      }

      isCartUrl(url) {
        try {
          const parsedUrl = new URL(url, window.location.origin);
          return parsedUrl.origin === window.location.origin && this.isCartPath(parsedUrl.pathname);
        } catch {
          return false;
        }
      }

      isCartPath(pathname) {
        const cartPath = (routes.cart_url || '/cart').replace(/\/$/, '');
        const currentPath = pathname.replace(/\/$/, '');
        return currentPath === cartPath;
      }

      escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value || '';
        return div.innerHTML;
      }
    }
  );
}
